import {
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Select,
  Switch,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import { useEffect, useState } from 'react';
import FormErrorMessageWithSpacing from '../../../components/form-error-message';
import FormModal from '../../../components/FormModal';
import FormSection from '../../../components/form-section';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { typography } from '../../../theme/tokens';
import {
  AccessControlConfigDto,
  AccessControlProviderType,
} from '../../../models/access-control/dto/access-control-config.dto';
import { OrganizationLocationDto } from '../../../models/location/dto/location.dto';
import { useUpsertAccessControlConfig } from '../hooks/useUpsertAccessControlConfig';
import {
  doorMappingsToRows,
  getProviderLabel,
  rowsToDoorMappings,
} from '../utils/access-control.utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  editingConfig?: AccessControlConfigDto | null;
  locations: OrganizationLocationDto[];
}

const ORG_WIDE_VALUE = '';

export default function AccessControlConfigModal({
  isOpen,
  onClose,
  organizationId,
  editingConfig,
  locations,
}: Props) {
  const toast = useToast();
  const [provider, setProvider] = useState<AccessControlProviderType>(
    AccessControlProviderType.ENZO_CARD,
  );
  const [locationId, setLocationId] = useState<string>(ORG_WIDE_VALUE);
  const [enabled, setEnabled] = useState(true);
  const [doorMappingRows, setDoorMappingRows] = useState(
    doorMappingsToRows({}),
  );
  const [settingsJson, setSettingsJson] = useState('{}');
  const [settingsError, setSettingsError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setProvider(editingConfig?.provider ?? AccessControlProviderType.ENZO_CARD);
    setLocationId(editingConfig?.locationId ?? ORG_WIDE_VALUE);
    setEnabled(editingConfig?.enabled ?? true);
    setDoorMappingRows(doorMappingsToRows(editingConfig?.doorMappings ?? {}));
    setSettingsJson(JSON.stringify(editingConfig?.settings ?? {}, null, 2));
    setSettingsError(null);
  }, [isOpen, editingConfig]);

  const { mutate, isLoading } = useUpsertAccessControlConfig(organizationId, () => {
    toast({
      title: translate('access_control.config_saved' as any),
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    onClose();
  });

  const handleAddRow = () => {
    setDoorMappingRows((rows) => [...rows, { courtId: '', doorId: '' }]);
  };

  const handleRemoveRow = (index: number) => {
    setDoorMappingRows((rows) => rows.filter((_, i) => i !== index));
  };

  const handleRowChange = (
    index: number,
    field: 'courtId' | 'doorId',
    value: string,
  ) => {
    setDoorMappingRows((rows) =>
      rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const handleSubmit = () => {
    let settings: Record<string, unknown>;
    try {
      settings = settingsJson.trim() === '' ? {} : JSON.parse(settingsJson);
    } catch {
      setSettingsError(translate('access_control.settings_invalid_json' as any));
      return;
    }
    setSettingsError(null);

    mutate(
      {
        provider,
        locationId: locationId || undefined,
        doorMappings: rowsToDoorMappings(doorMappingRows),
        settings,
        enabled,
      },
      {
        onError: (error: any) => {
          toast({
            title: translate('access_control.config_save_error' as any),
            description: error?.message || '',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        },
      },
    );
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={
        editingConfig
          ? translate('access_control.edit_config' as any)
          : translate('access_control.add_config' as any)
      }
      onSubmit={handleSubmit}
      submitLabel={translate('common.save' as any)}
      cancelLabel={translate('common.cancel' as any)}
      isSubmitting={isLoading}
    >
      <FormStack density="section">
        <FormSection columns={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel {...typography.formLabel}>
              {translate('access_control.provider' as any)}
            </FormLabel>
            <Select
              value={provider}
              onChange={(e) => setProvider(e.target.value as AccessControlProviderType)}
            >
              {Object.values(AccessControlProviderType).map((p) => (
                <option key={p} value={p}>
                  {getProviderLabel(p)}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel {...typography.formLabel}>
              {translate('access_control.location' as any)}
            </FormLabel>
            <Select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value={ORG_WIDE_VALUE}>
                {translate('access_control.org_wide' as any)}
              </option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </Select>
          </FormControl>
        </FormSection>

        <FormControl display="flex" alignItems="center" gap={3}>
          <Switch
            isChecked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            colorScheme="brand"
            aria-label={translate('access_control.enabled' as any)}
          />
          <FormLabel mb={0}>{translate('access_control.enabled' as any)}</FormLabel>
        </FormControl>

        <FormSection label={translate('access_control.door_mappings' as any)}>
          <Box w="100%">
            {doorMappingRows.map((row, index) => (
              <Box key={index} display="flex" gap={2} mb={2} alignItems="center">
                <Input
                  placeholder={translate('access_control.court_id' as any)}
                  value={row.courtId}
                  onChange={(e) => handleRowChange(index, 'courtId', e.target.value)}
                  size="sm"
                />
                <Input
                  placeholder={translate('access_control.door_id' as any)}
                  value={row.doorId}
                  onChange={(e) => handleRowChange(index, 'doorId', e.target.value)}
                  size="sm"
                />
                <IconButton
                  aria-label={translate('access_control.remove_mapping' as any)}
                  icon={<CloseIcon boxSize={2} />}
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRemoveRow(index)}
                />
              </Box>
            ))}
            <Button
              size="sm"
              leftIcon={<AddIcon boxSize={2} />}
              variant="outline"
              onClick={handleAddRow}
            >
              {translate('access_control.add_mapping' as any)}
            </Button>
          </Box>
        </FormSection>

        <FormControl isInvalid={!!settingsError}>
          <FormLabel {...typography.formLabel}>
            {translate('access_control.advanced_settings' as any)}
          </FormLabel>
          <Textarea
            value={settingsJson}
            onChange={(e) => setSettingsJson(e.target.value)}
            fontFamily="mono"
            fontSize="sm"
            rows={4}
          />
          <FormErrorMessageWithSpacing title={settingsError || ''} />
          <Text fontSize="xs" color="gray.500" mt={1}>
            {translate('access_control.advanced_settings_helper' as any)}
          </Text>
        </FormControl>
      </FormStack>
    </FormModal>
  );
}
